
import React, { useState, useMemo, useEffect } from 'react';
import { getVerifiedPipes } from '../engine/pipeDatabase';
import {
  calculateVelocity,
  calculateReynoldsNumber,
  calculateDarcyFrictionFactor,
} from '../engine/hydraulics';
import { getWaterProperties } from '../engine/fluids';
import { useLanguage } from '../i18n/LanguageContext';
import { getCopperCalculatorText } from '../i18n/copperCalculatorTranslations';
import { CopperPipeReportData } from './PrintableReport';

interface CopperPipeCalculatorProps {
  unitSystem: 'metric' | 'imperial';
  onPrint?: () => void;
  onReportDataChange?: (data: CopperPipeReportData | null) => void;
}

export const CopperPipeCalculator: React.FC<CopperPipeCalculatorProps> = ({
  unitSystem,
  onPrint,
  onReportDataChange,
}) => {
  const { language } = useLanguage();
  const t = getCopperCalculatorText(language);
  const isHindi = language === 'hi';

  const [service, setService] = useState<
    'domestic_cold' | 'domestic_hot' | 'heating_primary' | 'chilled_water'
  >('domestic_cold');
  const [rawFlow, setRawFlow] = useState<string>('0.45');
  const [rawLength, setRawLength] = useState<string>('15');

  const flowLps = parseFloat(rawFlow);
  const lengthM = parseFloat(rawLength);

  const validationError = useMemo(() => {
    if (isNaN(flowLps) || flowLps <= 0) return t.invalidFlow;
    if (isNaN(lengthM) || lengthM <= 0) return t.invalidLength;
    return null;
  }, [flowLps, lengthM, t]);

  const maxVelocity = useMemo(() => {
    switch (service) {
      case 'domestic_hot': return 1.2;
      case 'heating_primary': return 1.0;
      case 'chilled_water': return 1.5;
      default: return 2.0;
    }
  }, [service]);

  const copperPipes = useMemo(() => {
    return getVerifiedPipes('copper_en1057');
  }, []);

  const comparisonRows = useMemo(() => {
    if (validationError) return [];
    const flowM3s = flowLps * 0.001;
    const tempC = service === 'domestic_hot' ? 60 : 15;
    const fluid = getWaterProperties(tempC);

    return copperPipes.slice(0, 6).map((p) => {
      const v = calculateVelocity(flowM3s, p.innerDiameterMm);
      const Re = calculateReynoldsNumber(v, p.innerDiameterMm, fluid.kinematicViscosity);
      const f = calculateDarcyFrictionFactor(Re, p.roughnessMm, p.innerDiameterMm);
      const dM = p.innerDiameterMm / 1000;
      const dpPer10m = (f * (10 / dM) * (0.5 * fluid.density * Math.pow(v, 2))) / 1000;

      let status = 'Recommended';
      if (v > maxVelocity) status = `Too Fast (> ${maxVelocity} m/s)`;
      else if (v < 0.4) status = 'Oversized';

      return {
        pipe: p,
        v,
        dpPer10m,
        status,
      };
    });
  }, [copperPipes, flowLps, maxVelocity, service, validationError]);

  const getStatusLabel = (status: string) => {
    if (status === 'Recommended') return t.recommended;
    if (status === 'Oversized') return t.oversized;
    if (status.startsWith('Too Fast')) {
      return isHindi
        ? `${t.tooFast} (> ${maxVelocity} m/s)`
        : status;
    }
    return status;
  };

  const getServiceLabel = () => {
    switch (service) {
      case 'domestic_hot': return t.domesticHot;
      case 'heating_primary': return t.heatingPrimary;
      case 'chilled_water': return t.chilledWater;
      default: return t.domesticCold;
    }
  };

  useEffect(() => {
    if (!onReportDataChange) return;

    if (validationError || comparisonRows.length === 0) {
      onReportDataChange(null);
      return;
    }

    onReportDataChange({
      service,
      serviceLabel: getServiceLabel(),
      rawFlow,
      rawLength,
      unitSystem,
      maxVelocity,
      rows: comparisonRows.map((r) => ({
        nominalSize: r.pipe.nominalSize,
        innerDiameterMm: r.pipe.innerDiameterMm,
        velocityMs: r.v,
        dpKPaPer10m: r.dpPer10m,
        status: r.status,
      })),
    });
  }, [
    validationError,
    comparisonRows,
    service,
    rawFlow,
    rawLength,
    unitSystem,
    maxVelocity,
    onReportDataChange,
  ]);

  return (
    <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
      <div className="max-w-3xl">
        <span className="text-xs font-bold uppercase tracking-wider text-amber-600 block mb-1">
          {t.badge}
        </span>

        <h2 className="font-display font-extrabold text-2xl text-[#10283D]">
          {t.title}
        </h2>

        <p className="text-sm text-slate-600 mt-1">
          {t.description}
        </p>
      </div>

      {validationError && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs font-semibold">
          ⚠️ {validationError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-slate-100">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t.serviceType}
          </label>

          <select
            value={service}
            onChange={(e) => setService(e.target.value as typeof service)}
            className="w-full p-2.5 rounded-xl border border-slate-300 text-sm font-medium bg-white"
          >
            <option value="domestic_cold">
              {t.domesticCold} (Max 2.0 m/s)
            </option>
            <option value="domestic_hot">
              {t.domesticHot} (Max 1.2 m/s)
            </option>
            <option value="heating_primary">
              {t.heatingPrimary} (Max 1.0 m/s - {t.quiet})
            </option>
            <option value="chilled_water">
              {t.chilledWater} (Max 1.5 m/s)
            </option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t.requiredFlow} ({unitSystem === 'metric' ? 'L/s' : 'GPM'})
          </label>

          <input
            type="number"
            step="0.05"
            value={rawFlow}
            onChange={(e) => setRawFlow(e.target.value)}
            className="w-full p-2.5 rounded-xl border border-slate-300 text-sm font-semibold"
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t.lengthRun} ({unitSystem === 'metric' ? 'm' : 'ft'})
          </label>

          <input
            type="number"
            step="1"
            value={rawLength}
            onChange={(e) => setRawLength(e.target.value)}
            className="w-full p-2.5 rounded-xl border border-slate-300 text-sm font-semibold"
          />
        </div>
      </div>

      {/* Comparison Grid */}
      <div className="pt-4">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
          {t.evaluationTitle}
        </h3>

        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 font-bold uppercase">
              <tr>
                <th className="p-3">{t.nominalSize}</th>
                <th className="p-3">{t.internalDia}</th>
                <th className="p-3">{t.flowVelocity}</th>
                <th className="p-3">{t.pressureLoss}</th>
                <th className="p-3">{t.compliance}</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {comparisonRows.map(({ pipe, v, dpPer10m, status }) => (
                <tr key={pipe.nominalSize} className="hover:bg-slate-50">
                  <td className="p-3 font-bold text-slate-900">
                    {pipe.nominalSize}
                  </td>

                  <td className="p-3 font-mono">
                    {pipe.innerDiameterMm.toFixed(1)} mm
                  </td>

                  <td className={`p-3 font-mono font-bold ${
                    v > maxVelocity ? 'text-rose-600' : 'text-slate-800'
                  }`}>
                    {v.toFixed(2)} m/s
                  </td>

                  <td className="p-3 font-mono">
                    {dpPer10m.toFixed(1)} kPa
                  </td>

                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded font-bold text-[11px] ${
                      status.startsWith('Too Fast')
                        ? 'bg-rose-100 text-rose-800'
                        : status === 'Oversized'
                        ? 'bg-slate-100 text-slate-600'
                        : 'bg-emerald-100 text-emerald-800'
                    }`}>
                      {getStatusLabel(status)}
                    </span>
                  </td>
                </tr>
              ))}

              {!validationError && comparisonRows.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-slate-500">
                    {isHindi ? 'कोई परिणाम उपलब्ध नहीं है।' : 'No results available.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-slate-400 mt-3">
          {isHindi
            ? `चुनी गई सेवा: ${getServiceLabel()}। वेग सीमा: ${maxVelocity} m/s।`
            : `Selected service: ${getServiceLabel()}. Velocity limit: ${maxVelocity} m/s.`}
        </p>

        {onPrint && (
          <div className="pt-4 mt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-slate-500 font-medium">
              {isHindi ? 'कॉपर पाइप साइज़िंग विनिर्देश रिपोर्ट तैयार करें' : 'Generate verified copper pipe sizing specification sheet'}
            </span>
            <button
              type="button"
              onClick={onPrint}
              disabled={!!validationError}
              className="py-2.5 px-5 rounded-2xl font-bold text-xs bg-[#10283D] hover:bg-[#1E3A5F] active:bg-[#087F9C] text-white shadow-md flex items-center gap-2 transition-all duration-200 border border-slate-700/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className="w-4 h-4 text-[#39BFD3]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              <span>PDF Report / Print Spec</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
